import React, { useEffect, useState } from "react";
import { Coins, Check, Clock, Sparkles, CreditCard, ArrowRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useAuth } from "../../contexts/AuthContext";
import { subscribeCreditTransactions } from "../../services/collavueStore";
import PageHeader from "../../components/collavue/PageHeader";

const CREDIT_PACKS = [
  {
    name: "Starter",
    audience: "Best for trying things out",
    price: "$5",
    credits: 100,
    note: "~$0.05 per credit",
    features: [
      "Instant delivery via Stripe checkout",
      "Works across Collavue features",
      "Email receipt",
    ],
  },
  {
    name: "Growth",
    audience: "Good value for active users",
    price: "$10",
    credits: 250,
    note: "~$0.04 per credit",
    features: [
      "Instant delivery via Stripe checkout",
      "Works across Collavue features",
      "Priority email support",
    ],
  },
  {
    name: "Scale",
    audience: "Best value",
    price: "$20",
    credits: 500,
    note: "~$0.04 per credit",
    features: [
      "Instant delivery via Stripe checkout",
      "Works across Collavue features",
      "Priority email support",
    ],
    highlighted: true,
  },
];

const COSTS = [
  ["Analyze website", 2],
  ["Generate campaign strategy", 5],
  ["Manual sync", "15–100"],
  ["Generate reply", 1],
  ["Train brand voice", 3],
];

export default function BillingPage() {
  const { user, userProfile, authTokenBearer } = useAuth();
  const [tx, setTx] = useState([]);
  const [processingKey, setProcessingKey] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const credits = Number(userProfile?.credits ?? 0);

  useEffect(() => {
    if (!user) return;
    return subscribeCreditTransactions(setTx);
  }, [user]);

  const createCheckoutSession = async (creditPack) => {
    try {
      setErrorMsg("");
      if (!user?.uid) {
        // LoginPage reads the `redirect` param — `next` was silently ignored
        // and dumped freshly-logged-in buyers on the dashboard instead.
        navigate(
          `/login?redirect=${encodeURIComponent(
            `/billing?pack=${creditPack.name}`
          )}`
        );
        return;
      }

      const requestBody = {
        query: creditPack.name,
        user_id: user.uid,
      };

      const idToken = await authTokenBearer();

      const endpoint =
        process.env.REACT_APP_CREATE_CHECKOUT_SESSION ||
        "https://us-central1-fluencertuber.cloudfunctions.net/createCheckoutSession";

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data?.url) {
          window.location.assign(data.url);
          return;
        }
        // A 200 without a checkout URL used to no-op silently — the button
        // just un-spun and nothing happened. Surface it instead.
        throw new Error("Checkout response was missing the redirect URL.");
      } else {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Checkout failed (${res.status}): ${text || "no message"}`
        );
      }
    } catch (err) {
      setErrorMsg("Something went wrong starting checkout. Please try again.");
      Sentry.captureException(err, {
        tags: { section: "billing_checkout" },
        extra: { message: String(err?.message || err) },
      });
    }
  };

  const handleBuy = async (pack) => {
    if (processingKey) return;
    setProcessingKey(pack.name);
    try {
      await createCheckoutSession(pack);
    } finally {
      setProcessingKey(null);
    }
  };

  const status = new URLSearchParams(location.search).get("status");

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <PageHeader
        title="Billing & credits"
        subtitle="Buy credits, view balance, and review history."
      />

      {status === "success" && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Payment succeeded. Credits will appear in your balance shortly.
        </div>
      )}
      {status === "cancel" && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Checkout was cancelled. No charge was made.
        </div>
      )}
      {errorMsg && (
        <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {errorMsg}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5 mb-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-xs uppercase font-semibold text-slate-500">Credits</div>
          <div className="mt-1 text-4xl font-bold inline-flex items-center gap-2">
            <Coins className="w-7 h-7 text-indigo-600" /> {credits}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Pick a pack below to top up.
          </p>
        </div>
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-xs uppercase font-semibold text-slate-500 mb-3">
            Action costs
          </div>
          <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
            {COSTS.map(([k, v]) => (
              <li key={k} className="flex items-center justify-between">
                <span>{k}</span>
                <span className="font-semibold">{v} credits</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mb-10">
        {CREDIT_PACKS.map((p) => {
          const isProcessing = processingKey === p.name;
          return (
            <div
              key={p.name}
              className={`relative rounded-2xl border p-6 bg-white ${
                p.highlighted
                  ? "border-indigo-500 shadow-lg ring-1 ring-indigo-100"
                  : "border-slate-200"
              }`}
            >
              {p.highlighted && (
                <span className="absolute -top-3 right-6 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-600 text-white">
                  <Sparkles className="w-3.5 h-3.5" />
                  Best value
                </span>
              )}
              <div className="text-sm font-semibold text-slate-700">{p.name}</div>
              <div className="text-xs text-slate-500 mt-1">{p.audience}</div>
              <div className="text-3xl font-bold mt-3">{p.price}</div>
              <div className="text-sm text-slate-700 mt-1">{p.credits} credits</div>
              <div className="text-xs text-slate-500">{p.note}</div>
              <ul className="mt-4 space-y-2 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleBuy(p)}
                disabled={isProcessing}
                className={`mt-6 w-full inline-flex justify-center items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold ${
                  p.highlighted
                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                    : "bg-slate-900 text-white hover:bg-slate-800"
                } ${isProcessing ? "opacity-80 cursor-not-allowed" : ""}`}
              >
                <CreditCard
                  className={`w-4 h-4 ${isProcessing ? "animate-pulse" : ""}`}
                />
                {isProcessing ? "Starting checkout..." : "Buy credits"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-semibold mb-3 inline-flex items-center gap-2">
          <Clock className="w-4 h-4" /> Recent credit activity
        </h2>
        {tx.length === 0 ? (
          <p className="text-sm text-slate-500">No activity yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {tx.map((t) => (
              <li
                key={t.id}
                className="py-2.5 flex items-center justify-between text-sm"
              >
                <div>
                  <div className="font-medium capitalize">
                    {String(t.reason || t.type).replace(/_/g, " ")}
                  </div>
                  <div className="text-xs text-slate-500">
                    {t.createdAt?.toDate
                      ? t.createdAt.toDate().toLocaleString()
                      : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-semibold ${t.type === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                    {t.type === "credit" ? "+" : "-"}
                    {t.amount}
                  </div>
                  <div className="text-xs text-slate-500">
                    Balance {t.balanceAfter}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
